import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import Plot from "components/plotly/LocalisedPlot";
import { CENTRED_PLAYHEAD_MARKER_SHAPE } from "components/plotly/playheadMarker";
import { useVideoControlState } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { Dayjs } from "dayjs";
import { useMaterialColor, useMaterials } from "hooks/useMaterials";
import { OverlayStage } from "hooks/usePostProcessingOverlay";
import type { Data, Layout } from "plotly.js";
import { useMemo, useState } from "react";
import {
  ModelPredictionDataPoint,
  ModelPredictionTimeSeries,
} from "src/store/api/generatedApi";
import { useScrapEyeMetadata } from "src/contexts/scrapEyeMetadata";
import {
  collapsePeakRunsToApexes,
  PeakApexMarker,
  PeakCandidateFrame,
} from "./peakApexMarkers";

const PHASE_LABELS = new Set(["lift", "drop", "carry", "idle"]);

// idle/carry are never emitted as events (the model decodes only lift/drop), so
// their per-frame argmax flag is noise — excluded from the argmax overlay.
const ARGMAX_EXCLUDED_LABELS = new Set(["idle", "carry"]);

const toPlotlyUtcString = (date: Date): string =>
  date.toISOString().slice(0, 19);

const DEFAULT_WINDOW_SECONDS = 300;
const GAP_THRESHOLD_MS = 5000;

const LABEL_GROUPS = [
  "phase",
  "material",
  "materialGrade",
  "area",
  "other",
] as const;
type LabelGroup = (typeof LABEL_GROUPS)[number];

const LABEL_GROUP_TITLES: Record<LabelGroup, string> = {
  phase: "Phase",
  material: "Material",
  materialGrade: "Material grade (raw)",
  area: "Area",
  other: "Other",
};

// An unpooled material's model_output row is both its raw and its pooled
// signal — the same number — so it satisfies both groups.
const getLabelGroups = (
  label: string,
  origin: ModelPredictionDataPoint["origin"],
  materialNames: Set<string>,
  areaNames: Set<string>
): LabelGroup[] => {
  if (origin === "pooled_material") return ["material"];
  if (origin === "pooled_material_component") return ["materialGrade"];
  if (materialNames.has(label)) return ["material", "materialGrade"];
  if (PHASE_LABELS.has(label)) return ["phase"];
  if (areaNames.has(label)) return ["area"];
  return ["other"];
};

interface ModelPredictionsChartProps {
  data: ModelPredictionTimeSeries;
  windowStart: Dayjs;
  windowSeconds?: number;
  activeStage: OverlayStage;
}

interface LabelTraces {
  label: string;
  origin: ModelPredictionDataPoint["origin"];
  solid: { x: Date[]; y: (number | null)[] };
  bridge: { x: Date[]; y: (number | null)[] };
  smoothed: { x: Date[]; y: (number | null)[] };
  argmax: { x: Date[]; y: number[] };
  peakCandidates: PeakCandidateFrame[];
}

const ARGMAX_FLAG: ModelPredictionDataPoint["postprocessed_flags"][number] =
  "argmax";

// A raw grade can share its name with the tenant material it pools into (a
// tenant's finest-grained global grade often keeps the tenant's own term),
// so traces are keyed by (origin, label), not label alone.
const traceKey = (
  label: string,
  origin: ModelPredictionDataPoint["origin"]
): string => `${origin}:${label}`;

const traceDisplayName = ({ label, origin }: LabelTraces): string =>
  origin === "pooled_material_component" ? `${label} (raw)` : label;

const emptyLabelTraces = (
  label: string,
  origin: ModelPredictionDataPoint["origin"]
): LabelTraces => ({
  label,
  origin,
  solid: { x: [], y: [] },
  bridge: { x: [], y: [] },
  smoothed: { x: [], y: [] },
  argmax: { x: [], y: [] },
  peakCandidates: [],
});

const buildTracesByKey = (
  series: ModelPredictionDataPoint[]
): Record<string, LabelTraces> => {
  const tracesByKey = new Map<string, LabelTraces>();
  const previousPointByKey = new Map<
    string,
    { ts: Date; probability: number }
  >();

  for (const point of series) {
    const key = traceKey(point.label, point.origin);
    const ts = new Date(point.frame_ts);
    const traces =
      tracesByKey.get(key) ?? emptyLabelTraces(point.label, point.origin);
    tracesByKey.set(key, traces);

    const previous = previousPointByKey.get(key);
    if (
      previous !== undefined &&
      ts.getTime() - previous.ts.getTime() > GAP_THRESHOLD_MS
    ) {
      traces.solid.x.push(new Date((previous.ts.getTime() + ts.getTime()) / 2));
      traces.solid.y.push(null);
      if (traces.bridge.x.length > 0) {
        traces.bridge.x.push(new Date(0));
        traces.bridge.y.push(null);
      }
      traces.bridge.x.push(previous.ts);
      traces.bridge.y.push(previous.probability);
      traces.bridge.x.push(ts);
      traces.bridge.y.push(point.probability);
    }
    traces.solid.x.push(ts);
    traces.solid.y.push(point.probability);

    const markerY = point.postprocessed_probability ?? point.probability;
    if (point.postprocessed_probability !== null) {
      traces.smoothed.x.push(ts);
      traces.smoothed.y.push(point.postprocessed_probability);
    }
    if (point.postprocessed_flags.includes(ARGMAX_FLAG)) {
      traces.argmax.x.push(ts);
      traces.argmax.y.push(markerY);
    }
    traces.peakCandidates.push({
      ts,
      y: markerY,
      prominence: point.peak?.prominence ?? null,
    });

    previousPointByKey.set(key, { ts, probability: point.probability });
  }

  return Object.fromEntries(tracesByKey);
};

const peakMarkerSizes = (prominences: number[]): number[] => {
  if (prominences.length === 0) return [];
  const finite = prominences.filter(Number.isFinite);
  const max = finite.length > 0 ? Math.max(...finite) : 0;
  if (!(max > 0)) return prominences.map(() => 8);
  return prominences.map((p) => (Number.isFinite(p) ? 6 + (p / max) * 10 : 8));
};

const rawLineTraces = (
  key: string,
  labelTraces: LabelTraces,
  color: string
): Partial<Data>[] => {
  const { solid, bridge } = labelTraces;
  const name = traceDisplayName(labelTraces);
  const result: Partial<Data>[] = [
    {
      x: solid.x.map(toPlotlyUtcString),
      y: solid.y,
      type: "scatter",
      mode: "lines",
      name,
      legendgroup: key,
      line: { color, width: 1.5 },
      connectgaps: false,
      hovertemplate: `<b>%{fullData.name}</b><br>Time: %{x}<br>Prob: %{y:.3f}<extra></extra>`,
    },
  ];
  if (bridge.x.length > 0) {
    result.push({
      x: bridge.x.map(toPlotlyUtcString),
      y: bridge.y,
      type: "scatter",
      mode: "lines",
      name,
      legendgroup: key,
      showlegend: false,
      line: { color, width: 1.5, dash: "dot" },
      connectgaps: false,
      hoverinfo: "skip",
    });
  }
  return result;
};

const baseLineTrace = (
  key: string,
  labelTraces: LabelTraces,
  color: string
): Partial<Data> => {
  const { smoothed, solid } = labelTraces;
  const usesSmoothed = smoothed.x.length > 0;
  const source = usesSmoothed ? smoothed : solid;
  const suffix = usesSmoothed ? " (smoothed)" : "";
  return {
    x: source.x.map(toPlotlyUtcString),
    y: source.y,
    type: "scatter",
    mode: "lines",
    name: traceDisplayName(labelTraces),
    legendgroup: key,
    line: { color, width: 1.5 },
    connectgaps: false,
    hovertemplate: `<b>%{fullData.name}${suffix}</b><br>Time: %{x}<br>Prob: %{y:.3f}<extra></extra>`,
  };
};

const argmaxMarkerTrace = (
  key: string,
  labelTraces: LabelTraces,
  color: string
): Partial<Data> => ({
  x: labelTraces.argmax.x.map(toPlotlyUtcString),
  y: labelTraces.argmax.y,
  type: "scatter",
  mode: "markers",
  name: traceDisplayName(labelTraces),
  legendgroup: key,
  showlegend: false,
  marker: { color, size: 6, symbol: "circle" },
  hovertemplate: `<b>%{fullData.name} (argmax)</b><br>Time: %{x}<extra></extra>`,
});

const peakApexMarkerTrace = (
  key: string,
  labelTraces: LabelTraces,
  apexes: PeakApexMarker[],
  color: string
): Partial<Data> => ({
  x: apexes.map((apex) => toPlotlyUtcString(apex.ts)),
  y: apexes.map((apex) => apex.y),
  type: "scatter",
  mode: "markers",
  name: traceDisplayName(labelTraces),
  legendgroup: key,
  showlegend: false,
  marker: {
    color,
    size: peakMarkerSizes(apexes.map((apex) => apex.prominence)),
    symbol: "triangle-up",
  },
  customdata: apexes.map((apex) => apex.prominence),
  hovertemplate: `<b>%{fullData.name} (peak)</b><br>Time: %{x}<br>Prominence: %{customdata:.3f}<extra></extra>`,
});

export const ModelPredictionsChart = ({
  data,
  windowStart,
  windowSeconds = DEFAULT_WINDOW_SECONDS,
  activeStage,
}: ModelPredictionsChartProps) => {
  const state = useVideoControlState();
  const theme = useTheme();
  const { getColor } = useMaterialColor();
  const { materials } = useMaterials();
  const availableMaterialNames = useMemo(
    () => new Set(materials?.map((m) => m.name) ?? []),
    [materials]
  );
  const { available_areas: availableAreas } = useScrapEyeMetadata();
  const availableAreaNames = useMemo(
    () => new Set(availableAreas.map((area) => area.toLowerCase())),
    [availableAreas]
  );

  const [visibleGroups, setVisibleGroups] = useState<
    Record<LabelGroup, boolean>
  >({
    phase: true,
    material: false,
    materialGrade: false,
    area: false,
    other: false,
  });

  const toggleGroup = (group: LabelGroup) => {
    setVisibleGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  const currentTimestamp = windowStart.add(state.currentTime, "second");
  const halfWindow = windowSeconds / 2;
  const visibleStart = currentTimestamp.subtract(halfWindow, "second");
  const visibleEnd = currentTimestamp.add(halfWindow, "second");

  const tracesByKey = useMemo(
    () => buildTracesByKey(data.series),
    [data.series]
  );

  const { phaseColors, fallbackColors } = useMemo(() => {
    const { blue, pink, lightblue, orange, green, red } = theme.palette.data;
    return {
      phaseColors: {
        carry: blue,
        lift: green,
        drop: red,
        idle: orange,
      },
      fallbackColors: [blue, pink, lightblue, orange, green, red],
    };
  }, [theme.palette.data]);

  const labelColorMap = useMemo((): Record<string, string> => {
    const colors: Record<string, string> = {};
    let fallbackIndex = 0;
    const sortedEntries = Object.entries(tracesByKey).sort(([a], [b]) =>
      a.localeCompare(b)
    );
    for (const [key, { label }] of sortedEntries) {
      if (availableMaterialNames.has(label)) {
        colors[key] = getColor(label);
      } else if (label in phaseColors) {
        colors[key] = phaseColors[label as keyof typeof phaseColors];
      } else {
        colors[key] =
          fallbackColors[fallbackIndex++ % fallbackColors.length] ?? "#1f77b4";
      }
    }
    return colors;
  }, [
    tracesByKey,
    availableMaterialNames,
    getColor,
    phaseColors,
    fallbackColors,
  ]);

  const traces = useMemo((): Partial<Data>[] => {
    const visibleEntries = Object.entries(tracesByKey).filter(
      ([, labelTraces]) =>
        getLabelGroups(
          labelTraces.label,
          labelTraces.origin,
          availableMaterialNames,
          availableAreaNames
        ).some((group) => visibleGroups[group])
    );

    if (activeStage === "raw") {
      return visibleEntries.flatMap(([key, labelTraces]) =>
        rawLineTraces(key, labelTraces, labelColorMap[key] ?? "")
      );
    }

    const baseLines = visibleEntries.map(([key, labelTraces]) =>
      baseLineTrace(key, labelTraces, labelColorMap[key] ?? "")
    );

    if (activeStage === "smoothed") return baseLines;

    if (activeStage === "argmax") {
      const markers = visibleEntries
        .filter(
          ([, labelTraces]) =>
            labelTraces.argmax.x.length > 0 &&
            !ARGMAX_EXCLUDED_LABELS.has(labelTraces.label)
        )
        .map(([key, labelTraces]) =>
          argmaxMarkerTrace(key, labelTraces, labelColorMap[key] ?? "")
        );
      return [...baseLines, ...markers];
    }

    const peakMarkers = visibleEntries
      .map(([key, labelTraces]) => ({
        key,
        labelTraces,
        apexes: collapsePeakRunsToApexes(labelTraces.peakCandidates),
      }))
      .filter(({ apexes }) => apexes.length > 0)
      .map(({ key, labelTraces, apexes }) =>
        peakApexMarkerTrace(key, labelTraces, apexes, labelColorMap[key] ?? "")
      );
    return [...baseLines, ...peakMarkers];
  }, [
    tracesByKey,
    availableMaterialNames,
    availableAreaNames,
    visibleGroups,
    labelColorMap,
    activeStage,
  ]);

  const layout = useMemo((): Partial<Layout> => {
    return {
      autosize: true,
      height: 300,
      margin: { l: 35, r: 0, t: 20, b: 80 },
      xaxis: {
        type: "date",
        range: [
          toPlotlyUtcString(visibleStart.toDate()),
          toPlotlyUtcString(visibleEnd.toDate()),
        ],
        showgrid: true,
        gridcolor: "#e0e0e0",
        tickformat: "%H:%M:%S",
        hoverformat: "%H:%M:%S",
        tickfont: { size: 9, color: "#888" },
      },
      yaxis: {
        range: [0, 1],
        showgrid: true,
        gridcolor: "#e0e0e0",
        fixedrange: true,
        tickfont: { size: 9, color: "#888" },
        tickformat: ".1f",
        title: { text: "Prob", standoff: 5 },
      },
      shapes: state.duration === 0 ? [] : [CENTRED_PLAYHEAD_MARKER_SHAPE],
      showlegend: true,
      legend: {
        orientation: "h",
        x: 0,
        y: -0.25,
        xanchor: "left",
        yanchor: "top",
        itemclick: "toggle",
        itemdoubleclick: "toggleothers",
      },
      hovermode: "closest",
      dragmode: "pan",
    };
  }, [visibleStart, visibleEnd, state.duration]);

  return (
    <Stack spacing={0.5}>
      <FormGroup row sx={{ justifyContent: "center" }}>
        {LABEL_GROUPS.map((group) => (
          <FormControlLabel
            key={group}
            control={
              <Checkbox
                size="small"
                checked={visibleGroups[group]}
                onChange={() => {
                  toggleGroup(group);
                }}
              />
            }
            label={
              <Typography variant="caption">
                {LABEL_GROUP_TITLES[group]}
              </Typography>
            }
          />
        ))}
      </FormGroup>
      <Plot
        data={traces}
        layout={layout}
        config={{ displayModeBar: false, responsive: true, scrollZoom: false }}
        style={{ width: "100%", overflow: "visible" }}
        useResizeHandler
      />
    </Stack>
  );
};
